/**
 * Centralised HTML sanitization for any content that comes from WooCommerce
 * (product descriptions, short descriptions, etc.).
 *
 * Defense-in-depth: even though product HTML is authored by the trusted shop
 * admin in WordPress, we sanitize it before rendering with React's
 * dangerouslySetInnerHTML so that a hypothetical WP compromise cannot turn
 * into stored XSS on the React storefront.
 *
 * We allow basic formatting + common WooCommerce markup (lists, links, tables,
 * line breaks, images) and strip <script>, <iframe>, on* handlers, etc.
 */
import DOMPurify from "isomorphic-dompurify";

const ALLOWED_TAGS = [
  "a", "abbr", "b", "blockquote", "br", "code", "div", "em", "h1", "h2", "h3",
  "h4", "h5", "h6", "hr", "i", "img", "li", "ol", "p", "pre", "span", "strong",
  "sub", "sup", "table", "tbody", "td", "tfoot", "th", "thead", "tr", "u", "ul",
];

const ALLOWED_ATTR = [
  "href", "title", "alt", "src", "srcset", "sizes", "width", "height",
  "target", "rel", "class", "id", "style",
];

const baseConfig = {
  ALLOWED_TAGS,
  ALLOWED_ATTR,
  // Force every link opened in a new tab to be safe
  ADD_ATTR: ["target"],
  // Block JS-bearing protocols and on* handlers (DOMPurify already does this,
  // but being explicit makes the intent obvious to reviewers).
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "style"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur"],
};

/** Sanitize HTML and return a string safe for dangerouslySetInnerHTML. */
export const sanitizeHtml = (dirty) => {
  if (!dirty) return "";
  return DOMPurify.sanitize(String(dirty), baseConfig);
};

/** Sanitize HTML and return the plain-text content (used for previews/SEO). */
export const sanitizeAndStrip = (dirty) => {
  if (!dirty) return "";
  return DOMPurify.sanitize(String(dirty), { ...baseConfig, ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
};
